import { ESystemTypes } from 'store/system/actionsTypes';
import { EPropCertificatesTypes } from './actionsTypes';
import { CertificatesAction, PropCertificate, PropCertificatesReducerState } from './interfaces';

const MISSING_EARNED_ON_TIME = Number.MIN_SAFE_INTEGER;

const getEarnedOnTime = (certificate: PropCertificate): number => {
    if (!certificate.earnedOn) {
        return MISSING_EARNED_ON_TIME;
    }

    const earnedOnTime = new Date(certificate.earnedOn).getTime();
    return Number.isNaN(earnedOnTime) ? MISSING_EARNED_ON_TIME : earnedOnTime;
};

const sortCertificatesNewestFirst = (certificates: PropCertificate[]): PropCertificate[] =>
    [...certificates].sort((first, second) => getEarnedOnTime(second) - getEarnedOnTime(first));

const initialState: PropCertificatesReducerState = {
    certificates: [],
    certificatesLoading: true,
    certificatesError: undefined,
    renderByCertId: {},
    shareLinkByCertId: {},
};

const PropCertificatesReducer = (state = initialState, action: CertificatesAction): PropCertificatesReducerState => {
    const { type } = action;

    switch (type) {
        case EPropCertificatesTypes.GET_CERTIFICATES_START:
            return {
                ...state,
                certificatesLoading: true,
                certificatesError: undefined,
            };
        case EPropCertificatesTypes.GET_CERTIFICATES_SUCCESS:
            return {
                ...state,
                certificates: sortCertificatesNewestFirst(action.payload.certificates),
                socialSharing: action.payload.socialSharing,
                isPayoutCertificationEnabled: action.payload.isPayoutCertificationEnabled,
                isChallengePassCertificationEnabled: action.payload.isChallengePassCertificationEnabled,
                certificatesLoading: false,
                certificatesError: undefined,
            };
        case EPropCertificatesTypes.GET_CERTIFICATES_FAIL:
            return {
                ...state,
                certificatesLoading: false,
                certificatesError: action.payload.error,
            };
        case EPropCertificatesTypes.RENDER_CERTIFICATE_START:
            return {
                ...state,
                renderByCertId: {
                    ...state.renderByCertId,
                    [action.payload.certId]: {
                        ...state.renderByCertId[action.payload.certId],
                        loading: true,
                        error: undefined,
                    },
                },
            };
        case EPropCertificatesTypes.RENDER_CERTIFICATE_SUCCESS: {
            const { certId, kind, pdfBase64, dataUrl } = action.payload;
            const previousRenderState = state.renderByCertId[certId];
            const nextRenderState = {
                ...previousRenderState,
                loading: false,
                error: undefined,
            };
            if (kind === 'pdf') {
                nextRenderState.pdfBase64 = pdfBase64;
            } else if (kind === 'image') {
                nextRenderState.imageDataUrl = dataUrl;
            } else {
                nextRenderState.previewDataUrl = dataUrl;
            }
            return {
                ...state,
                renderByCertId: {
                    ...state.renderByCertId,
                    [certId]: nextRenderState,
                },
            };
        }
        case EPropCertificatesTypes.RENDER_CERTIFICATE_FAIL:
            return {
                ...state,
                renderByCertId: {
                    ...state.renderByCertId,
                    [action.payload.certId]: {
                        ...state.renderByCertId[action.payload.certId],
                        loading: false,
                        error: action.payload.error,
                    },
                },
            };
        case EPropCertificatesTypes.GET_THUMBNAILS_START: {
            const renderByCertId = { ...state.renderByCertId };
            action.payload.certIds.forEach((certId) => {
                renderByCertId[certId] = {
                    ...renderByCertId[certId],
                    loading: true,
                    error: undefined,
                };
            });
            return { ...state, renderByCertId };
        }
        case EPropCertificatesTypes.GET_THUMBNAILS_SUCCESS: {
            const renderByCertId = { ...state.renderByCertId };
            const thumbnailDataUrlByCertId = new Map(
                action.payload.thumbnails.map((thumbnail) => [thumbnail.certId, thumbnail.thumbnailDataUrl])
            );
            action.payload.certIds.forEach((certId) => {
                const previousRenderState = renderByCertId[certId];
                const thumbnailDataUrl = thumbnailDataUrlByCertId.get(certId) || previousRenderState?.thumbnailDataUrl;
                renderByCertId[certId] = {
                    ...previousRenderState,
                    loading: false,
                    error: thumbnailDataUrl ? undefined : 'Failed to load certificate thumbnail',
                    thumbnailDataUrl,
                };
            });
            return { ...state, renderByCertId };
        }
        case EPropCertificatesTypes.GET_THUMBNAILS_FAIL: {
            const renderByCertId = { ...state.renderByCertId };
            action.payload.certIds.forEach((certId) => {
                const previousRenderState = renderByCertId[certId];
                renderByCertId[certId] = {
                    ...previousRenderState,
                    loading: false,
                    error: previousRenderState?.thumbnailDataUrl ? undefined : action.payload.error,
                };
            });
            return { ...state, renderByCertId };
        }
        case EPropCertificatesTypes.GET_SHARE_LINK_SUCCESS:
            return {
                ...state,
                shareLinkByCertId: {
                    ...state.shareLinkByCertId,
                    [action.payload.certId]: action.payload.shareUrl,
                },
            };
        case ESystemTypes.RESET_APP:
            return initialState;
        default:
            return state;
    }
};

export default PropCertificatesReducer;
